import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Form, Row, Spin, Table } from 'antd'
import BaseListing from 'src/BaseListings'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import WidgetFiscalYearSelector from 'src/widgets/Selectors/WidgetFiscalYearSelector'
import WidgetSelector from 'src/widgets/Selectors/WidgetSelector'
import moment from 'moment'

const base = new BaseListing()
const WPPF_ELIGIBILITY_OPTIONS = [
  { value: 'all', label: 'Show All' },
  { value: 'eligible', label: 'Eligible for WPPF' },
  { value: 'not_eligible', label: 'Not Eligible' },
]

const WppfReport = () => {
  const companyName = wtGetFromLocalStorage('company_name')
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [fiscalYears, setFiscalYears] = useState([])
  const [branches, setBranches] = useState([])
  const [fiscalYearId, setFiscalYearId] = useState(0)
  const [fiscalYearName, setFiscalYearName] = useState('')
  const [branchId, setBranchId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const [wppfEligibility, setWppfEligibility] = useState('all')
  const [reportData, setReportData] = useState([])

  useEffect(() => {
    base.dropdownData('fiscal_years,branches', {}, [setFiscalYears, setBranches], 1)
  }, [])

  const changeValues = (key, value) => {
    setReportData([])

    if (key === 'fiscal_year_id') {
      setFiscalYearId(value.value)
      setFiscalYearName(value.label)
    }

    if (key === 'branch_id') {
      setBranchId(value.value)
      setBranchName(value.label)
    }

    if (key === 'wppf_eligibility') {
      setWppfEligibility(value.value)
    }
  }

  const validateFilters = () => {
    if (+fiscalYearId === 0) {
      lzNotifyErrorMessageFromResult('Please select a fiscal year!')
      return false
    }

    if (+branchId === 0) {
      lzNotifyErrorMessageFromResult('Please select a branch!')
      return false
    }

    return true
  }

  const getReportData = () => {
    if (!validateFilters()) {
      return
    }

    setIsLoading(true)
    wtApiCall(
      'reports/wppf_report',
      {
        fiscal_year_id: fiscalYearId,
        branch_id: branchId,
        wppf_eligibility: wppfEligibility,
      },
      'get',
      (response) => {
        setReportData(response?.data?.rows || [])
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const exportToExcel = () => {
    if (!validateFilters()) {
      return
    }

    setIsLoading(true)
    wtApiPostWithContentTypeHandling('reports/wppf_report/export', {
      fiscal_year_id: fiscalYearId,
      branch_id: branchId,
      wppf_eligibility: wppfEligibility,
    }).finally(() => {
      setIsLoading(false)
    })
  }

  const columns = [
    {
      title: 'Sr',
      key: 'sr',
      width: 50,
      fixed: 'left',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 90,
      fixed: 'left',
      render: (value) => (
        <a href={`/employee-profile?id=${value}`} target="_blank" rel="noreferrer">
          {value}
        </a>
      ),
    },
    {
      title: 'Personal File No.',
      dataIndex: 'personal_file_no',
      key: 'personal_file_no',
      width: 140,
      fixed: 'left',
    },
    {
      title: 'Full Name',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 220,
      fixed: 'left',
    },
    {
      title: 'Branch',
      dataIndex: 'branch',
      key: 'branch',
      width: 170,
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      width: 170,
    },
    {
      title: 'Designation',
      dataIndex: 'designation',
      key: 'designation',
      width: 170,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
    },
    {
      title: 'Mobile no.',
      dataIndex: 'mobile_no',
      key: 'mobile_no',
      width: 140,
    },
    {
      title: 'CNIC',
      dataIndex: 'cnic',
      key: 'cnic',
      width: 160,
    },
    {
      title: 'Date Of Joining',
      dataIndex: 'date_of_joining',
      key: 'date_of_joining',
      width: 140,
      render: (value) => (value ? moment(value).format('DD-MMM-YYYY') : '-'),
    },
    {
      title: 'Last Working Day',
      dataIndex: 'last_working_day',
      key: 'last_working_day',
      width: 140,
      render: (value) => (value ? moment(value).format('DD-MMM-YYYY') : '-'),
    },
    {
      title: 'Is Rejoining',
      dataIndex: 'is_rejoining',
      key: 'is_rejoining',
      width: 120,
    },
    {
      title: 'Eligible for WPPF',
      dataIndex: 'eligible_for_wppf',
      key: 'eligible_for_wppf',
      width: 140,
      render: (value) => (
        <span style={{ color: value === 'No' ? '#cf1322' : '#389e0d', fontWeight: 700 }}>
          {value}
        </span>
      ),
    },
    {
      title: 'Duration (Current Year)',
      dataIndex: 'duration_current_year',
      key: 'duration_current_year',
      width: 180,
    },
  ]

  return (
    <div className="container-fluid" style={{ padding: '1em', backgroundColor: 'white', minHeight: '100vh' }}>
      <Form layout="vertical" form={form} onFinish={getReportData}>
        <Card
          title={
            <span style={{ fontSize: '22px', display: 'block', textAlign: 'center' }}>
              {companyName} WPPF Report
            </span>
          }
        >
          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} sm={12} md={5}>
              <label>Fiscal Year *</label>
              <WidgetFiscalYearSelector
                value={fiscalYearId}
                displayValue={fiscalYearName}
                fiscalYears={fiscalYears}
                onChange={(values) => changeValues('fiscal_year_id', values)}
                reloadDropdownData={() => base.dropdownData('fiscal_years', {}, [setFiscalYears], 1)}
                size="sm"
                displayMaxWidth="100%"
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <label>Branch *</label>
              <WidgetBranchSelector
                value={branchId}
                displayValue={branchName}
                branches={branches}
                onChange={(values) => changeValues('branch_id', values)}
                reloadDropdownData={() => base.dropdownData('branches', {}, [setBranches], 1)}
                size="sm"
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <label>WPPF Eligibility</label>
              <WidgetSelector
                selectedValue={wppfEligibility}
                displayValue=""
                options={WPPF_ELIGIBILITY_OPTIONS}
                onChange={(values) => changeValues('wppf_eligibility', values)}
                placeHolder="Select WPPF Eligibility"
                showCloudIcon={false}
                size="sm"
                displayMaxWidth="100%"
              />
            </Col>
            <Col xs={24} sm={24} md={9} style={{ display: 'flex', alignItems: 'end', gap: 8 }}>
              <Button type="primary" htmlType="submit" disabled={isLoading}>
                Generate Report
              </Button>
              {base.hasRights('Export WPPF Report') && (
                <Button
                  style={{ backgroundColor: '#4BB543', color: '#fff' }}
                  onClick={exportToExcel}
                  disabled={isLoading}
                >
                  Export
                </Button>
              )}
            </Col>
          </Row>

          <br />

          <Table
            bordered
            columns={columns}
            dataSource={reportData}
            loading={{ indicator: <Spin />, spinning: isLoading }}
            pagination={false}
            rowKey="id"
            scroll={{ x: 'max-content', y: 600 }}
            size="small"
          />
        </Card>
      </Form>
    </div>
  )
}

export default WppfReport
